/**
 * What a person may do with one file, here — the single answer every view of
 * the workspace (Browse, Search, the Desktop) renders from. Pure: no React.
 *
 * Built on the client mirror of the file-area policy (`vault-areas.ts`) plus
 * the person's role on the workspace. Like that mirror, it only lets the UI
 * offer what will work; the SERVER re-decides every operation
 * (`vaultAreaPolicy`, `assertPrincipalCanActForHolder`) and trusts none of
 * this.
 */
import type { VaultDocumentDto } from '@/lib/api';
import type { HolderSwitcherOption } from '@/components/HolderSwitcher';
import { vaultAreaAllows, type VaultArea } from '@/lib/vault-areas';

/** The person's standing on the workspace. `null` = not known (the page was
 *  rendered without a switcher option) — offer everything and let the server
 *  decide, which is how the page behaved before roles were read here. */
export interface FileActorContext {
  holderType: HolderSwitcherOption['holderType'] | null;
  role: HolderSwitcherOption['role'] | null;
}

export function actorContextOf(
  holder: Pick<HolderSwitcherOption, 'holderType' | 'role'> | null | undefined,
): FileActorContext {
  return { holderType: holder?.holderType ?? null, role: holder?.role ?? null };
}

export function isViewer(ctx: FileActorContext): boolean {
  return ctx.role === 'viewer';
}

/** May this person change what's in folders here at all (move, upload,
 *  create folders)? A viewer only looks. */
export function canChangeMembership(ctx: FileActorContext): boolean {
  return !isViewer(ctx);
}

export interface FileCapabilities {
  /** Move to another folder (the placement controller still decides where). */
  move: boolean;
  rename: boolean;
  /** Move to the Trash. */
  trash: boolean;
  /** Trashing asks first: the file is an arche's own (My-Fias), and the arche
   *  may rely on it. */
  trashNeedsConfirm: boolean;
  restore: boolean;
  purge: boolean;
  giveArcheAccess: boolean;
}

type CapabilityDoc = Pick<VaultDocumentDto, 'deletedAt' | 'visibility' | 'sensitivity'>;

export function fileCapabilities(
  doc: CapabilityDoc,
  area: VaultArea,
  ctx: FileActorContext,
): FileCapabilities {
  const trashed = !!doc.deletedAt;
  const writer = !isViewer(ctx);
  // My-Fias in a shared workspace is state every member's arche relies on:
  // only owners and admins may delete there (`AREA_ADMIN_REQUIRED`).
  const sharedWorkspace = ctx.holderType !== null && ctx.holderType !== 'personal';
  const mayDeleteHere =
    area !== 'my-fias' ||
    !sharedWorkspace ||
    ctx.role === null ||
    ctx.role === 'owner' ||
    ctx.role === 'admin';
  return {
    move: !trashed && writer && area !== 'my-fias',
    rename: !trashed && writer && vaultAreaAllows(area, 'rename-file'),
    trash: !trashed && writer && vaultAreaAllows(area, 'delete-file') && mayDeleteHere,
    trashNeedsConfirm: area === 'my-fias',
    restore: trashed && writer,
    purge: trashed && writer,
    // Only the user's own files can be shared with an arche, and never a
    // proprietary one (the server refuses both anyway).
    giveArcheAccess:
      !trashed && writer && doc.visibility === 'user-vault' && doc.sensitivity !== 'proprietary',
  };
}
